/******/ (() => { // webpackBootstrap
/******/ 	"use strict";

;// ./src/javascripts/utils.js
/**
 * Throttle execution of function
 *
 * @param {Function} callback function to be throttled
 * @param {number} interval milliseconds
 * @returns {Function}
 */
function throttle(callback, interval) {
  let enableCall = true;
  let calledWhenDisabled = false;


  return (...args) =>{
    const onTimeout = () => {
      if (calledWhenDisabled) {
        callback.apply(this, args);
        setTimeout(onTimeout, interval);
      } else {
        enableCall = true;
      }
      calledWhenDisabled = false;
    }

    if (!enableCall) {
      calledWhenDisabled = true;
      return;
    }

    enableCall = false;
    callback.apply(this, args);
    setTimeout(onTimeout, interval);
  }
}

/**
 * Take monetary value and format it as money
 *
 * @param {number | string} cents - Price in cents (hundreds of base unit)
 * @param {string} [format] - Format for money; store format will be used if undefined
 * @return {string} - HTML string with the formatted money value
 */
function formatMoney(cents, format) {
  var moneyFormat = format || '${{amount}}';
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = moneyFormat;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (precision === null || precision === undefined) {
      precision = 2;
    }
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) {
      return '0';
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.');
    var dollarsAmount = parts[0].replace(
      /(\d)(?=(\d{3})+(?!\d))/g,
      '$1' + thousands
    );
    var centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  var match = formatString.match(placeholderRegex);

  if (!match) {
    throw new Error(
      `Invalid format string: '${formatString}'. Expected '{{amount}}' or similar placeholders.`
    );
  }

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    default:
      throw new Error(`Unknown format type: ${match[1]}`);
  }

  return formatString.replace(placeholderRegex, value);
}

// FocusTrap class for managing focus within a container
class FocusTrap {
  /**
   * @param {HTMLElement} container - The container within which to trap focus.
   * @param {HTMLElement} [initialElement=null] - The element to focus initially. If not provided, the first focusable element is focused.
   * @param {Function} [onEscape=null] - Optional callback to execute when the Escape key is pressed.
   */
  constructor(container, initialElement = null, onEscape = null) {
    this.container = container;
    this.initialElement = initialElement;
    this.onEscape = onEscape;
    this.focusableElements = [];
    this.firstElement = null;
    this.lastElement = null;
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.updateFocusableElements = this.updateFocusableElements.bind(this);
    this.observer = new MutationObserver(this.updateFocusableElements);
    this.init();
  }

  /**
   * Initializes the focus trap by removing the 'inert' attribute,
   * updating focusable elements, setting initial focus, and attaching necessary event listeners.
   */
  init() {
    if (!this.container) return;

    // Remove the 'inert' attribute from the container
    this.container.removeAttribute('inert');

    this.updateFocusableElements();

    if (this.initialElement && this.isFocusable(this.initialElement)) {
      this.initialElement.focus();
    } else if (this.firstElement) {
      this.firstElement.focus();
    }

    this.container.addEventListener('keydown', this.handleKeyDown);
    this.observer.observe(this.container, { childList: true, subtree: true, attributes: true });
  }

  /**
   * Determines if an element is focusable.
   * @param {HTMLElement} el - The element to check.
   * @returns {boolean} - True if the element is focusable, else false.
   */
  isFocusable(el) {
    if (!el) return false;
    return (
      el.offsetParent !== null &&
      el.getAttribute('tabindex') !== '-1' &&
      !this.isInsideClosedDetails(el)
    );
  }

  /**
   * Determines if an element is inside a closed <details> element.
   * Allows <summary> elements to remain focusable even if their parent <details> is closed.
   * @param {HTMLElement} el - The element to check.
   * @returns {boolean} - True if inside a closed <details> and not a <summary>, else false.
   */
  isInsideClosedDetails(el) {
    let parent = el.parentElement;
    while (parent) {
      if (parent.tagName.toLowerCase() === 'details') {
        if (!parent.hasAttribute('open')) {
          // Allow the <summary> element itself to be focusable
          if (el.tagName.toLowerCase() === 'summary' && parent.querySelector('summary') === el) {
            return false;
          }
          return true;
        }
      }
      parent = parent.parentElement;
    }
    return false;
  }

  /**
   * Updates the list of focusable elements, excluding those inside closed <details> (except <summary>).
   */
  updateFocusableElements() {
    this.focusableElements = Array.from(this.container.querySelectorAll(
      'a[href], area[href], input:not([type=hidden]), select, textarea, button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable], summary'
    )).filter(el => this.isFocusable(el));

    if (this.focusableElements.length > 0) {
      this.firstElement = this.focusableElements[0];
      this.lastElement = this.focusableElements[this.focusableElements.length - 1];
    } else {
      this.firstElement = null;
      this.lastElement = null;
      console.warn('No focusable elements found within the container.');
    }
  }

  /**
   * Handles the keydown event to trap focus within the container.
   * @param {KeyboardEvent} e - The keyboard event.
   */
  handleKeyDown(e) {
    if (e.key === 'Tab') {
      if (this.focusableElements.length === 0) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey) { // Shift + Tab
        if (document.activeElement === this.firstElement) {
          e.preventDefault();
          this.lastElement.focus();
        }
      } else { // Tab
        if (document.activeElement === this.lastElement) {
          e.preventDefault();
          this.firstElement.focus();
        }
      }
    } else if (e.key === 'Escape') {
      if (this.onEscape) {
        this.onEscape();
      }
    }
  }

  /**
   * Destroys the focus trap by removing event listeners and disconnecting observers.
   */
  destroy() {
    this.container.setAttribute('inert', '');
    this.container.removeEventListener('keydown', this.handleKeyDown);
    this.observer.disconnect();
  }
}

;// ./src/javascripts/webcomponents/filtering.js


class CollectionFiltersForm extends HTMLElement {
  // Define class properties for bound functions
  handleSortByChangeBound = this.handleSortByChange.bind(this);
  stopInputPropagationBound = this.stopInputPropagation.bind(this);

  constructor() {
    super();
    this.filterData = [];
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.productGridId = 'CollectionProductGrid';
    this.filterFormsId = 'CollectionFiltersForm';
    this.searchTemplate = document.querySelector('body.template-search');
    this.collectionTemplate = document.querySelector('body.template-collection');
    this.sections = this.getSections();
    this.addDrawerAttributes = this.addDrawerAttributes.bind(this);
    this.buttonAriaLabel = this.getAttribute('data-button-aria-label');
    this.filterForm = this.querySelector(`#${this.filterFormsId}`);
    this.sortByDesktop = this.querySelector('#SortBydesktop');
    this.sortByMobile = this.querySelector('#SortBymobile');
    this.sortByInput = this.querySelector('#sort_by_input');

    // Keep hidden sort_by in sync with the visible select; empty sort_by in AJAX requests
    // makes Shopify recalculate facets differently than the initial full-page render (missing options).
    if (this.sortByInput && this.sortByDesktop) {
      this.sortByInput.value = this.sortByDesktop.value;
    }

    if (window.innerWidth > 991) {
      this.filterForm.removeAttribute('inert');
    }

    this.debouncedOnSubmit = this.debounce(this.onSubmitHandler.bind(this), 500);

    // Add event listeners using bound functions
    if (this.sortByDesktop && this.sortByMobile) {
      this.sortByDesktop.addEventListener('change', this.handleSortByChangeBound);
      this.sortByMobile.addEventListener('change', this.handleSortByChangeBound);

      this.sortByDesktop.addEventListener('input', this.stopInputPropagationBound);
      this.sortByMobile.addEventListener('input', this.stopInputPropagationBound);
    }

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit);
    // Optimistic UI: update the per-filter count bubble (the little number next to the
    // filter label in the summary) as soon as the user toggles a checkbox / commits a
    // price, instead of waiting ~700-1200 ms for the debounced AJAX response to re-render
    // the filter HTML. We sync duplicates first so desktop + mobile copies stay consistent
    // with the checkbox the user just touched.
    this.querySelector('form').addEventListener('change', (event) => {
      this.syncFacetCheckboxDuplicates(event.currentTarget, event);
      this.updateOptimisticCounts();
    });
    // Safety net: stop any native form submit (Enter key, programmatic submit) from doing
    // a full page reload. Price inputs already preventDefault on Enter via their keydown
    // handler; this guard covers other paths (e.g. programmatic HTMLFormElement.submit()).
    this.querySelector('form').addEventListener('submit', (event) => {
      event.preventDefault();
    });
    document.querySelector('[data-drawer-open-btn]').addEventListener('click', this.handleDrawerOpen.bind(this));
    document.querySelector('[data-drawer-close-btn]').addEventListener('click', this.handleDrawerClose.bind(this));
    const drawerOverlay = document.querySelector('[data-filtering-drawer-overlay]');
    if (drawerOverlay) drawerOverlay.addEventListener('click', this.handleDrawerClose.bind(this));
    window.addEventListener('resize', this.addDrawerAttributes);

    // History: refetch sections on popstate when the user goes back/forward within the same
    // collection (e.g. after AJAX filters + pushState). Do NOT refetch when the page was
    // restored from bfcache (back from PDP): pageshow.persisted means the DOM snapshot is
    // already correct; refetching replaced the grid with section HTML that could show the wrong
    // variant / AGOTADO vs what was on screen before leaving.
    this._skipNextHistoryFetch = false;
    this.refreshFromHistory = this.debounce(() => {
      this.filterData = [];
      const searchParams = new URLSearchParams(window.location.search).toString();
      this.renderPage(searchParams, null, false);
    }, 0);
    this.onPopStateBound = () => {
      if (this._skipNextHistoryFetch) {
        this._skipNextHistoryFetch = false;
        return;
      }
      this.refreshFromHistory();
    };
    this.onPageShowBound = (e) => {
      if (e.persisted) {
        this._skipNextHistoryFetch = true;
      }
    };
    window.addEventListener('popstate', this.onPopStateBound);
    window.addEventListener('pageshow', this.onPageShowBound);

    this.addDrawerAttributes();
    this.bindActiveFacetButtonEvents();
    this.onDropDownBlur();
  }

  // Define debounce as a class method
  debounce = (fn, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  };

  // Define stopInputPropagation as a class method
  stopInputPropagation(event) {
    event.stopPropagation();
  }

  /**
   * Stable comparison for filter query strings: drops empty values, drops sort_by when it
   * matches the store default (same as omitting sort in the URL on first paint), sorts entries.
   * Stops spurious form `input` events from re-fetching the section when nothing actually changed.
   */
  normalizeComparableParams(paramString) {
    const params = new URLSearchParams(paramString);
    const defaultSort = this.getAttribute('data-default-sort');
    const pairs = [];
    for (const [key, value] of params.entries()) {
      if (value === '' || value == null) continue;
      if (key === 'sort_by' && defaultSort != null && defaultSort !== '' && value === defaultSort) {
        continue;
      }
      pairs.push([key, value]);
    }
    pairs.sort((a, b) => {
      const c = a[0].localeCompare(b[0]);
      return c !== 0 ? c : String(a[1]).localeCompare(String(b[1]));
    });
    return JSON.stringify(pairs);
  }

  isFormStateEquivalentToCurrentUrl(formParamsString) {
    const loc = window.location.search.startsWith('?')
      ? window.location.search.slice(1)
      : window.location.search;
    return this.normalizeComparableParams(loc) === this.normalizeComparableParams(formParamsString);
  }

  /**
   * The theme renders the same facet block twice (desktop + mobile) inside one form.
   * Unchecking only updates the visible checkbox; the duplicate stays checked, so FormData
   * still sends the filter and the AJAX response "restores" the selection.
   */
  syncFacetCheckboxDuplicates(form, event) {
    const useDesktop = window.innerWidth > 991;
    const groups = new Map();
    form.querySelectorAll('input[type="checkbox"][name]').forEach((cb) => {
      const key = `${cb.name}\0${cb.value}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(cb);
    });
    groups.forEach((cbs, key) => {
      if (cbs.length < 2) return;
      let master = null;
      const t = event && event.target;
      if (t && t.type === 'checkbox' && t.name && cbs.includes(t)) {
        const tk = `${t.name}\0${t.value}`;
        if (tk === key) master = t;
      }
      if (!master) {
        master = cbs.find((cb) => {
          const inner = cb.closest('.facets__form-inner');
          if (!inner) return false;
          return useDesktop ? inner.classList.contains('desktop') : inner.classList.contains('mobile');
        });
      }
      if (!master) master = cbs[0];
      const checked = master.checked;
      cbs.forEach((cb) => {
        if (cb.checked !== checked) cb.checked = checked;
      });
    });
  }

  /**
   * Recalculate every filter's count bubble (<span class="count-bubble">) from the
   * current state of its inputs, so the user sees the number change instantly when they
   * toggle a checkbox or commit a price. The authoritative count still comes from the
   * server; when renderFilters() replaces the filter HTML, the count bubble text will be
   * rewritten with the server value (which should match what we computed here).
   *
   * Logic mirrors the Liquid in snippets/main-collection-filtering-sorting.liquid and
   * sections/template--search.liquid:
   *   - list / boolean filter → count of checked checkboxes inside the <details>.
   *   - price filter (contains <price-range-selector>) → 1 if any input has a value, else 0.
   */
  updateOptimisticCounts() {
    const detailsList = this.querySelectorAll('.js-filter');
    detailsList.forEach((details) => {
      const bubble = details.querySelector('summary .count-bubble');
      if (!bubble) return;

      let count = 0;
      const priceSelector = details.querySelector('price-range-selector');
      if (priceSelector) {
        const inputs = priceSelector.querySelectorAll('input');
        const hasValue = Array.from(inputs).some((input) => {
          const raw = input.dataset.rawValue != null ? input.dataset.rawValue : input.value;
          return (raw || '').toString().trim() !== '';
        });
        count = hasValue ? 1 : 0;
      } else {
        count = details.querySelectorAll('input[type="checkbox"]:checked').length;
      }

      const next = count > 0 ? String(count) : '';
      if (bubble.textContent !== next) bubble.textContent = next;
    });
  }

  onDropDownBlur() {
    // Remove the previous listener before adding a new one to avoid accumulation
    // (this method is called on every AJAX render, so without cleanup listeners pile up)
    if (this._dropdownBlurHandler) {
      document.removeEventListener('click', this._dropdownBlurHandler);
    }

    this._dropdownBlurHandler = (event) => {
      // `event.target` can become detached from the DOM between click and this handler
      // (e.g. if a price input was just replaced by an AJAX render), which makes
      // `item.contains(event.target)` return false and wrongly collapses the open
      // disclosure the user is trying to type into. Falling back to `document.activeElement`
      // keeps the disclosure that has focus open in that edge case.
      const active = document.activeElement;
      document.querySelectorAll('.facets__disclosure').forEach(item => {
        if (item.contains(event.target)) return;
        if (active && active !== document.body && item.contains(active)) return;
        item.removeAttribute('open');
      });
    };

    document.addEventListener('click', this._dropdownBlurHandler);
  }

  isHidden(el) {
    return (el.offsetParent === null);
  }

  addDrawerAttributes() {
    const filterDrawer = document.querySelector('collection-filtering-form');
    filterDrawer.removeAttribute('role');
    filterDrawer.removeAttribute('aria-modal');
    filterDrawer.removeAttribute('aria-label');

    if (window.innerWidth < 991) {
      // Only set `inert` when the drawer is closed. If we blindly re-apply it on
      // every `resize`, iOS Safari locks the whole drawer as soon as the virtual
      // keyboard opens (typing in the price "Desde" / "Hasta" inputs triggers a
      // resize event while the drawer is open, which previously re-added `inert`
      // to the form and left every filter untappable until the drawer was closed).
      const drawerOpen = document.body.classList.contains('js-drawer-open-filter');
      if (!drawerOpen) {
        this.filterForm.setAttribute('inert', '');
      }
      filterDrawer.setAttribute('role', 'dialog');
      filterDrawer.setAttribute('aria-modal', 'true');
      filterDrawer.setAttribute('aria-label', this.buttonAriaLabel);
    } else {
      this.filterForm.removeAttribute('inert');
    }
  }

  handleDrawerOpen(e) {
    const filterDrawer = this.querySelector('#CollectionFiltersForm');
  
    document.body.classList.add('js-drawer-open-filter', 'js-drawer-open');
    this.focusTrap = new FocusTrap(filterDrawer, null, this.handleDrawerClose.bind(this));
  }
  
  handleDrawerClose() {
    const filterBtn = document.querySelector('[data-drawer-open-btn]');
    document.body.classList.remove('js-drawer-open-filter', 'js-drawer-open');
  
    filterBtn.setAttribute('tabIndex', '0');
    filterBtn.focus();
  
    if (this.focusTrap) {
      this.focusTrap.destroy();
      this.focusTrap = null;
    }
  }

  handleSortByChange(event) {
    event.preventDefault();
    event.stopPropagation();

    const newValue = event.target.value;

    if (!this.sortByInput) {
      console.warn('Sort by input element not found');
      return;
    }

    // Update the hidden input
    this.sortByInput.value = newValue;

    // Determine which select was changed and update the other accordingly
    if (event.target === this.sortByDesktop) {
      // Update SortBymobile without triggering its change event
      if (this.sortByMobile.value !== newValue) {
        this.sortByMobile.value = newValue;
      }
    } else if (event.target === this.sortByMobile) {
      // Update SortBydesktop without triggering its change event
      if (this.sortByDesktop.value !== newValue) {
        this.sortByDesktop.value = newValue;
      }
    }

    // Dispatch the input event to trigger form's input listener
    this.sortByInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Syncs duplicate price range inputs (desktop + mobile copies of the same filter) so all
   * copies carry the master's value before FormData is built. Without this the URL ends up
   * with e.g. `price_min=&price_min=150000` and Shopify uses the first (empty) value.
   * We identify price inputs by living inside a <price-range-selector>, which is stable
   * across `type="number"` vs `type="text"` and across theme updates.
   */
  syncPriceRangeInputs(form, event) {
    const groups = new Map();
    form.querySelectorAll('price-range-selector input[name]').forEach(input => {
      const key = input.name;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(input);
    });

    groups.forEach((inputs) => {
      if (inputs.length < 2) return;

      const t = event && event.target;
      let master = (t && inputs.includes(t)) ? t : null;
      if (!master) master = inputs.find(i => i.value !== '') || inputs[0];

      const val = master.value;
      const raw = master.dataset.rawValue || '';
      inputs.forEach(input => {
        if (input.value !== val) input.value = val;
        input.dataset.rawValue = raw;
      });
    });
  }

  /**
   * Replace comma-formatted display values (e.g. "100,000") with their raw numeric form
   * ("100000") in the FormData. Shopify's storefront filter URL params
   * (filter.v.price.gte / filter.v.price.lte) take the amount in the store's MAJOR unit
   * (pesos, dollars, yen) — NOT subunits/cents — so we send the digits as-is. The display
   * side still has to divide `filter.max_value.value` by `currency_subunit` because Liquid
   * returns those values in subunits; only the URL side of the API uses the major unit.
   */
  normalizePriceFormData(formData, form) {
    form.querySelectorAll('price-range-selector input[name]').forEach(input => {
      const name = input.getAttribute('name');
      if (!name) return;
      const raw = (input.dataset.rawValue != null && input.dataset.rawValue !== '')
        ? input.dataset.rawValue
        : String(input.value || '').replace(/\D/g, '');
      formData.set(name, raw);
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();

    // event.target may be a detached node if renderMobileElements replaced the mobile form
    // before the 500 ms debounce fired (the old input is removed from the DOM at that point).
    // isConnected guards against that; we fall back to this.filterForm so the submission
    // still goes through with the correct form data.
    const form = (event.target && event.target.isConnected)
      ? event.target.closest('form')
      : this.filterForm;

    if (!form) return;

    this.syncFacetCheckboxDuplicates(form, event);
    this.syncPriceRangeInputs(form, event);

    if (this.collectionTemplate) {
      const formData = new FormData(form);
      this.normalizePriceFormData(formData, form);
      // Theme uses sort_by_desktop / sort_by_mobile (or sort_bydesktop on search) for UI only;
      // only `sort_by` is a real storefront param. Sending empty sort_by + duplicates changes facet results.
      formData.delete('sort_by_desktop');
      formData.delete('sort_by_mobile');
      formData.delete('sort_bydesktop');
      formData.delete('sort_bymobile');
      const searchParams = new URLSearchParams(formData).toString();
      if (this.isFormStateEquivalentToCurrentUrl(searchParams)) {
        return;
      }
      this.renderPage(searchParams, event);
    } else if (this.searchTemplate) {
      // Store the initial URL so that we can get the search query from it to add to the formData object
      const searchParamsInitial = new URLSearchParams(window.location.search);

      // Get the selected result types, we will need to use them later on
      const activeTypes = document.querySelector('#page-type-input').value;
      const types = Array.from(activeTypes.split(','));
      const defaultTypes = document.querySelector('.search--header input[name="type"]');

      if (activeTypes === "") {
        const getInput = document.querySelector('#page-type-input');
        getInput.value = defaultTypes.value;
      }

      // Add the initial search query to the formData object otherwise the query will be lost and we will get an error
      const formData = new FormData(form);
      this.normalizePriceFormData(formData, form);
      formData.append('q', searchParamsInitial.get('q'));
      formData.delete('sort_by_desktop');
      formData.delete('sort_by_mobile');
      formData.delete('sort_bydesktop');
      formData.delete('sort_bymobile');

      // If the result type is not of type product, we want to disable the filter inputs and remove product type queries from the URL
      if ((types.includes('article') && !types.includes('product')) || (types.includes('page') && !types.includes('product'))) {
        const getInputs = document.querySelectorAll(`#${this.filterFormsId} input:not(#page-type-input)`);
        const getLabels = document.querySelectorAll(`#${this.filterFormsId} label:not(.collection-filters__label)`);

        // Disable the inputs if the result type is article or page
        for (let item of getInputs) {
          item.setAttribute('disabled', 'disabled');
        }

        for (let inputLabel of getLabels) {
          inputLabel.classList.add('facet-checkbox--disabled');
        }

        // If our result is an article or page type, remove all product related filters from the URL
        if (searchParamsInitial.has('filter.p.product_type')) {
          formData.delete('filter.p.product_type');
        }

        if (searchParamsInitial.has('filter.p.vendor')) {
          formData.delete('filter.p.vendor');
        }

        if (searchParamsInitial.has('filter.v.availability')) {
          formData.delete('filter.v.availability');
        }
      }

      const searchParams = new URLSearchParams(formData).toString();
      if (this.isFormStateEquivalentToCurrentUrl(searchParams)) {
        return;
      }
      this.renderPage(searchParams, event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    event.currentTarget.setAttribute('loading', 'true');
    this.toggleActiveFacets();
    this.renderPage(new URL(event.currentTarget.href).searchParams.toString());
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  renderPage(searchParams, event, updateURLHash = true) {
    document.querySelector(`#${this.productGridId}>div`).classList.add('loading');

    this.sections.forEach((section) => {
      if (this.collectionTemplate) {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        const filterDataUrl = element => element.url === url;

        this.filterData.some(filterDataUrl) ?
          this.renderSectionFromCache(filterDataUrl, section, event) :
          this.renderSectionFromFetch(url, section, event);
      } else if (this.searchTemplate) {
        const url = `${window.location.pathname}?${searchParams}`;
        const filterDataUrl = element => element.url === url;

        this.filterData.some(filterDataUrl) ?
          this.renderSectionFromCache(filterDataUrl, section, event) :
          this.renderSectionFromFetch(url, section, event);
      }
    });

    if (updateURLHash) this.updateURLHash(searchParams);
  }

  renderSectionFromFetch(url, section, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        this.filterData = [...this.filterData, { html, url }];

        switch (section.id) {
          case this.productGridId:
            this.renderProductGrid(html);
            break;
          case this.filterFormsId:
            this.renderFilters(html);
            break;
          default:
            return;
        }
      });
  }

  renderSectionFromCache(filterDataUrl, section, event) {
    const cachedData = this.filterData.find(filterDataUrl);
    if (!cachedData) return;

    const html = cachedData.html;
    this.renderFilters(html);
    this.renderProductGrid(html);
  }

  renderProductGrid(html) {
    const innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById(this.productGridId).innerHTML;

    document.getElementById(this.productGridId).innerHTML = innerHTML;

    // Force a scroll to trigger show-on-scroll load in animations
    window.scrollBy(0, 1);
    window.scrollBy(0, -1);

    const event = new CustomEvent('paginate');
    document.dispatchEvent(event);
  }

  renderFilters(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // Update Desktop Filters — sync every .js-filter from the section response.
    // The server HTML never emits `open`, so we remember which desktop disclosures
    // were expanded before the innerHTML swap and restore them afterwards. Without
    // this, any AJAX render that lands while the user has a filter open collapses
    // it, which reads as "the dropdown keeps closing when I try to interact".
    const openDesktopIndexes = new Set();
    document.querySelectorAll('.facets__form-inner.desktop .js-filter[open]').forEach(el => {
      openDesktopIndexes.add(el.dataset.index);
    });

    const facetDetailsElements = parsedHTML.querySelectorAll(`#${this.filterFormsId} .js-filter`);

    facetDetailsElements.forEach((element) => {
      const targetFilter = document.querySelector(`.facets__form-inner.desktop .js-filter[data-index="${element.dataset.index}"]`);
      if (targetFilter) {
        targetFilter.innerHTML = element.innerHTML;
      } else {
        console.warn(`Target filter not found for data-index: ${element.dataset.index}`);
      }
    });

    openDesktopIndexes.forEach(index => {
      const el = document.querySelector(`.facets__form-inner.desktop .js-filter[data-index="${index}"]`);
      if (el) el.setAttribute('open', '');
    });

    // Update Active Facets
    this.renderActiveFacets(parsedHTML);

    // Update Mobile Elements
    this.renderMobileElements(parsedHTML);

    this.bindSortByEventListeners();
  }

  bindSortByEventListeners() {
    this.sortByDesktop = this.querySelector('#SortBydesktop');
    this.sortByMobile = this.querySelector('#SortBymobile');

    if (this.sortByDesktop && this.sortByMobile) {
      // Remove existing listeners using bound functions
      this.sortByDesktop.removeEventListener('change', this.handleSortByChangeBound);
      this.sortByMobile.removeEventListener('change', this.handleSortByChangeBound);

      this.sortByDesktop.removeEventListener('input', this.stopInputPropagationBound);
      this.sortByMobile.removeEventListener('input', this.stopInputPropagationBound);

      // Re-attach event listeners
      this.sortByDesktop.addEventListener('change', this.handleSortByChangeBound);
      this.sortByMobile.addEventListener('change', this.handleSortByChangeBound);

      this.sortByDesktop.addEventListener('input', this.stopInputPropagationBound);
      this.sortByMobile.addEventListener('input', this.stopInputPropagationBound);
    }
  }

  renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
      } else {
        console.warn(`Active facets element not found for selector: ${selector}`);
      }
    });

    this.bindActiveFacetButtonEvents();
    this.toggleActiveFacets(false);
  }

  renderMobileElements(html) {
    // Save which mobile filter dropdowns are currently open so we can restore them after
    // the innerHTML replacement (the server HTML never includes the `open` attribute).
    const openMobileIndexes = new Set();
    document.querySelectorAll('.facets__form-inner.mobile .js-filter[open]').forEach(el => {
      openMobileIndexes.add(el.dataset.index);
    });

    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.facets__form-inner.mobile'
    ];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);

      if (sourceElement && targetElement) {
        targetElement.innerHTML = sourceElement.innerHTML;
      } else {
        console.warn(`Element not found for selector: ${selector}`);
      }
    });

    // Restore the open state for any filter that was open before the replacement
    openMobileIndexes.forEach(index => {
      const el = document.querySelector(`.facets__form-inner.mobile .js-filter[data-index="${index}"]`);
      if (el) el.setAttribute('open', '');
    });

    // Re-bind event listeners for mobile-specific elements
    this.bindActiveFacetButtonEvents();
    this.onDropDownBlur();
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      // Remove existing listeners to prevent duplicates
      element.removeEventListener('click', this.onActiveFilterClick);
      // Add event listener
      element.addEventListener('click', this.onActiveFilterClick, { once: true });
    });
  }

  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams ? '?'.concat(searchParams) : ''}`);
  }

  getSections() {
    return [
      {
        id: this.productGridId,
        section: document.getElementById(this.productGridId).dataset.sectionId
      },
      {
        id: this.filterFormsId,
        section: document.getElementById('main-collection-filters').dataset.id
      }
    ];
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.addDrawerAttributes);
    window.removeEventListener('popstate', this.onPopStateBound);
    window.removeEventListener('pageshow', this.onPageShowBound);
    if (this._dropdownBlurHandler) {
      document.removeEventListener('click', this._dropdownBlurHandler);
    }
  }
}

customElements.define('collection-filtering-form', CollectionFiltersForm);

/**
 * <price-range-selector> — two text inputs (Desde / Hasta) that:
 *   - Display the value with thousand separators (e.g. 100,000) as the user types.
 *   - Keep the raw integer in input.dataset.rawValue so FormData can submit a clean
 *     `price_min=100000` (Shopify rejects comma-separated values).
 *   - Only commit the filter (AJAX submit of the parent form) on `change`, NOT on every
 *     keystroke: the mobile filter gets re-rendered after each submit, which destroyed
 *     the input the user was typing in and caused focus/value loss.
 *   - Validate against the real numeric min/max (stored in data-min / data-max on the
 *     input, set by Liquid as plain integers). The previous implementation read them
 *     off the HTML `min` / `max` attributes which were rendered with a locale-aware dot
 *     that JS parsed as a decimal, clamping 100000 to 711.5.
 */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.inputs = Array.from(this.querySelectorAll('input'));
    this.inputs.forEach(input => {
      const raw = this.onlyDigits(input.dataset.rawValue || input.value);
      input.dataset.rawValue = raw;
      input.value = raw;

      input.addEventListener('input', this.onInput.bind(this));
      input.addEventListener('change', this.onRangeChange.bind(this));
      input.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    // Price filtering is explicit: only the "Aplicar" button (or Enter in an input)
    // triggers the AJAX submit. Typing and blurring never fire a submit on their own,
    // so the filter HTML can't re-render while the user is still entering a value.
    this.applyButton = this.querySelector('.facets__price-apply');
    if (this.applyButton) {
      this.applyButton.addEventListener('click', this.onApplyClick.bind(this));
    }

    this.setMinAndMaxValues();
  }

  onlyDigits(str) {
    return String(str == null ? '' : str).replace(/\D/g, '');
  }

  /**
   * Every keystroke stays local: we stopPropagation unconditionally so the form's
   * `input` listener (which runs the debounced AJAX submit for checkboxes) never sees
   * typing in a price input. stopImmediatePropagation would also silence any other
   * listener on the same input in the future. Submission only happens through apply().
   */
  onInput(event) {
    event.stopPropagation();
    const input = event.currentTarget;
    const cleaned = this.onlyDigits(input.value);

    if (input.value !== cleaned) {
      const removed = input.value.length - cleaned.length;
      const caret = Math.max(0, (input.selectionStart || 0) - removed);
      input.value = cleaned;
      try { input.setSelectionRange(caret, caret); } catch (e) { /* not a text input */ }
    }
    input.dataset.rawValue = cleaned;

    const collection = this.closest('collection-filtering-form');
    if (collection && typeof collection.updateOptimisticCounts === 'function') {
      collection.updateOptimisticCounts();
    }
  }

  onApplyClick(event) {
    event.preventDefault();
    this.apply();
  }

  /**
   * Enter behaves like clicking "Aplicar": commit and submit. preventDefault avoids a
   * native form submit (full page reload).
   */
  onKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.apply();
    }
  }

  /**
   * Blur / native change just snaps values into range; it does NOT submit. Submission
   * is explicit via apply(). stopPropagation prevents the form's change listener from
   * doing unnecessary work (checkbox sync) for price inputs.
   */
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  /**
   * Explicit submit path: clamp both inputs, keep desktop/mobile copies in sync, then
   * call the parent CollectionFiltersForm's submit handler directly (skipping the
   * debounce so results appear immediately — the user already waited to click).
   * On mobile the filter drawer is dismissed so the user sees the filtered collection
   * without extra taps.
   */
  apply() {
    this.inputs.forEach(input => this.adjustToValidValues(input));
    this.setMinAndMaxValues();

    const collection = this.closest('collection-filtering-form');
    if (!collection || typeof collection.onSubmitHandler !== 'function') return;

    // Mirror the values the user just entered into every other price input with the
    // same name across the form (the theme renders desktop + mobile copies). Without
    // this, the max-group's `syncPriceRangeInputs` picks the stale sibling as "master"
    // (because event.target is the min input, not the max) and overwrites the freshly
    // entered empty "Hasta" with an old value like "120000" — the user then sees that
    // stale value in the URL, in the results, and back in the drawer on the next open.
    const form = collection.querySelector('form');
    if (form) {
      this.inputs.forEach(src => {
        if (!src.name) return;
        form.querySelectorAll('price-range-selector input').forEach(dst => {
          if (dst === src || dst.name !== src.name) return;
          dst.value = src.value;
          dst.dataset.rawValue = src.dataset.rawValue || '';
        });
      });
    }

    const firstInput = this.inputs[0];
    collection.onSubmitHandler({
      target: firstInput,
      preventDefault: () => {},
    });

    if (document.body.classList.contains('js-drawer-open-filter')
      && typeof collection.handleDrawerClose === 'function') {
      collection.handleDrawerClose();
    }
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.inputs;
    if (!minInput || !maxInput) return;
    const absoluteMax = maxInput.dataset.max || '';
    const minRaw = minInput.dataset.rawValue || '';
    const maxRaw = maxInput.dataset.rawValue || '';

    minInput.dataset.max = maxRaw || absoluteMax;
    minInput.dataset.min = '0';
    maxInput.dataset.max = absoluteMax;
    maxInput.dataset.min = minRaw || '0';
  }

  adjustToValidValues(input) {
    const raw = this.onlyDigits(input.value);
    if (!raw) {
      input.dataset.rawValue = '';
      input.value = '';
      return;
    }
    const value = Number(raw);
    const min = Number(input.dataset.min || '0');
    const max = Number(input.dataset.max || '0');

    let clamped = value;
    if (!isNaN(min) && value < min) clamped = min;
    if (!isNaN(max) && max > 0 && value > max) clamped = max;

    const clampedStr = String(clamped);
    input.dataset.rawValue = clampedStr;
    input.value = clampedStr;
  }
}

customElements.define('price-range-selector', PriceRange);

class PageType extends HTMLElement {
  constructor() {
    super()
    this.selector = document.querySelector('[data-page-type-selector]');
    this.selector.addEventListener('change', this.setValue);
  }

  setValue(e) {
    const selectedTypes = e.currentTarget.querySelectorAll('input:checked');
    const types = Array.from(selectedTypes).map(option => option.value).join(',');

    const typeInput = document.querySelector('#page-type-input');
    typeInput.value = types;

    const event = new Event('input');
    typeInput.closest('form').dispatchEvent(event);
  }
}

customElements.define('page-type-selector', PageType);

/******/ })()
;